/**
 * BUG-S2169-001 — ขอบวันของ "ใกล้หมดอายุ / หมดอายุแล้ว" บนแดชบอร์ดการเช่า
 *
 * เดิมคำนวณ `Math.floor((expected_return_date - now) / 1 วัน)` → สัญญาที่ครบกำหนด **วันนี้**
 * ได้ -1 จึงถูกนับว่า "หมดอายุแล้ว" · ครบกำหนด **พรุ่งนี้** ได้ 0 แล้วหลุดทั้งสองการ์ด (เงื่อนไข > 0)
 * ขณะที่หน้ารายการ `?status=expiring` (backend `is_expiring_soon`) นับ วันนี้ ≤ วันครบกำหนด ≤ วันนี้+30
 * → การ์ดบอก 0 แต่กดลิงก์แล้วเจอ 1 แถว
 *
 * นิยามเดียวกับ backend: เทียบเป็น "วันปฏิทิน" ไม่ใช่เวลา
 *   expiring = 0 ≤ วันที่เหลือ ≤ days · expired = วันที่เหลือ < 0
 */
import { getLocalDateString } from './dateUtils';

const dayNumber = (ymd: string): number => {
  const [y, m, d] = ymd.slice(0, 10).split('-').map(Number);
  return Date.UTC(y, m - 1, d) / 86400000;
};

/** จำนวนวันปฏิทินจาก "วันนี้" ถึงวันครบกำหนด (ค่าลบ = เลยกำหนดแล้ว) · null = ไม่มีวันที่ */
export function daysUntilDue(expectedReturnDate?: string | null, today: string = getLocalDateString()): number | null {
  if (!expectedReturnDate) return null;
  const ymd = String(expectedReturnDate).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  return dayNumber(ymd) - dayNumber(today);
}

export type RentalExpiryBucket = 'expiring' | 'expired' | null;

export function rentalExpiryBucket(
  rental: { status?: string | null; expected_return_date?: string | null },
  today: string = getLocalDateString(),
  days = 30,
): RentalExpiryBucket {
  if (rental.status?.toLowerCase() !== 'active') return null;
  const left = daysUntilDue(rental.expected_return_date, today);
  if (left === null) return null;
  if (left < 0) return 'expired';
  return left <= days ? 'expiring' : null;
}

/**
 * BUG-S2338-001 — ป้าย "เหลือ" ในตารางใกล้หมดอายุบนแดชบอร์ด (รายการเช่า + สัญญา)
 * backend ส่ง `days_remaining` แบบ inclusive: ครบกำหนด **วันนี้** = 0 (ยังอยู่ในหน้าต่าง "ใกล้หมดอายุ")
 * เดิมจอใช้ `days <= 0` → "หมดอายุแล้ว" สีแดง ทั้งที่ยังไม่หมด และ `value || 0` ทำให้ค่าว่างกลายเป็น "หมดอายุแล้ว" ด้วย
 */
export type DaysRemainingTone = 'danger' | 'warning' | 'success' | 'muted';

export function daysRemainingLabel(days: number | null | undefined): { text: string; tone: DaysRemainingTone } {
  if (days === null || days === undefined || Number.isNaN(Number(days))) return { text: '—', tone: 'muted' };
  const n = Number(days);
  if (n < 0) return { text: 'หมดอายุแล้ว', tone: 'danger' };
  if (n === 0) return { text: 'ครบกำหนดวันนี้', tone: 'danger' };
  if (n <= 7) return { text: `${n} วัน`, tone: 'danger' };
  if (n <= 30) return { text: `${n} วัน`, tone: 'warning' };
  return { text: `${n} วัน`, tone: 'success' };
}
